import React, { forwardRef } from "react";
import CustomIconButton from "./CustomIconButton";
import {DEFAULT_INPUT_MAX_LENGTH} from "../constants/index";

const TimeInput = forwardRef(
  (
    {
      type = "text",
      name,
      onClearQuery,
      onChange,
      error = false,
      errorText,
      size = "lg",
      placeholder,
      searchIcon = false,
      startIcon = null,
      endIcon = null,
      maxLength = DEFAULT_INPUT_MAX_LENGTH,
      customClassName,
      ...props
    }: any,
    ref
  ) => {
    let styleBasedSizeProp = {
      lg: `${searchIcon ? "py-2 h-9" : "py-3 h-12"} px-3 text-sm`,
      base: "px-3 py-[0.55rem] text-sm",
      md: "px-3 py-[0.375rem] h-9 text-sm",
      filter: "px-2 py-1 h-[2rem] text-xs"
    } as any;

    let styleBasedErrorProp = !error
      ? "border border-[#E0E0E0] active:border-[#FF861A] focus:border-[#FF861A]"
      : "border-2 border-[#F33D62] active:border-[#F33D62] focus:border-[#F33D62]";

      const handleInternalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
      
        const cleanedValue = value.replace(/\D/g, "");
      
        if (cleanedValue === "") {
          onChange(name, "");
          return;
        }
      
        if (cleanedValue.length > maxLength) {
          return;
        }
      
        const num = parseInt(cleanedValue, 10);
        if (num >= 0 && num <= 99) {
          onChange(name, cleanedValue);
        }
      };

    return (
      <>
        <div className={`relative group ${!props.disabled && "hover:bg-[#FFF3E5]"}`}>
          <input
            ref={ref} // Use the forwarded ref here
            name={name}
            type={type}
            maxLength={maxLength}
            className={`custom-textinput ${styleBasedSizeProp[size]} w-full outline-0 ${
              !props.disabled ? styleBasedErrorProp : "border border-[#E0E0E0]"
            } ${
              props.disabled
                ? ""
                : "focus:hover:bg-[#FFF3E5] hover:border-transparent hover:bg-[#FFF3E5] bg-white active:text-[#343A3D] text-[#333]"
            }  rounded ${searchIcon ? "pl-[2.25rem] pr-8" : ""} ${
              startIcon !== null ? "pl-[2.25rem]" : ""
            } ${endIcon !== null ? "pr-6" : ""} ${customClassName}`}
            placeholder={placeholder}
            onChange={handleInternalChange}
            {...props}
            value={props.value ?? ''}
            onKeyDown={(e) => {
              const allowedKeys = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete", "Enter"];
              
              if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault();
              }
            }}
          />
          {searchIcon && (
            <span className="absolute top-[0.25rem] left-2 bottom-[0.25rem] flex items-center justify-center">
              <svg
                width={size==="filter" ? "20" : "24"}
                height={size==="filter" ? "20" : "24"}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_8574_3270)">
                  <path
                    d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z"
                    fill="#808080"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_8574_3270">
                    <rect width="24" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </span>
          )}
          {/* clear text ICON */}
          {searchIcon && props.value && (
            <CustomIconButton
              size="sm"
              onClick={() => onClearQuery()}
              customStyle="cursor-pointer absolute top-[50%] right-2 translate-y-[-50%] flex items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
              >
                <g clipPath="url(#clip0_8574_265)">
                  <path
                    d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                    fill="currentColor"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_8574_265">
                    <rect width="24" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </CustomIconButton>
          )}
          {startIcon && (
            <button
              onClick={startIcon?.onClick}
              className="absolute top-3 bottom-3 left-2"
            >
              {startIcon?.icon}
            </button>
          )}
          {endIcon && (
            <button
              onClick={endIcon?.onClick}
              className="hidden sm:block absolute top-2 bottom-2 right-2 text-[#808080] group-hover:text-[#FF861A]"
            >
              {endIcon?.icon}
            </button>
          )}
        </div>
        {(error && errorText) && (
          <p className="alu-text-error text-sm mt-2">
            {errorText ? `*${errorText}` : ""}
          </p>
        )}
      </>
    );
  }
);

export default TimeInput;
